import { useEffect, useRef, useState } from 'react';
import { ANALYTICS_EVENTS } from '../../constants/analytics';
import { useWorkspaceId } from '../../contexts/WorkspaceContext';
import { useMobileDetection } from '../../hooks/ui/useMobileDetection';
import { safeAnalytics } from '../../utils/analytics';
import { AddEquipmentFormStep } from './AddEquipmentFormStep';
import { AddEquipmentOptionsStep, type EquipmentScan } from './AddEquipmentOptionsStep';
import { AddEquipmentQrStep } from './AddEquipmentQrStep';
import { AddEquipmentSearchStep, type EquipmentSearchResult } from './AddEquipmentSearchStep';
import {
  EMPTY_EQUIPMENT_FORM,
  type AddEquipmentStep,
  type EquipmentFormState,
} from './addEquipmentForm';
import { useAddEquipment } from './useAddEquipment';

export interface AddEquipmentPanelProps {
  /** Collection to create the equipment in. Omit for the workspace root. */
  readonly parentCollectionId?: string;
  /** The equipment was created; the caller closes (the panel then navigates). */
  readonly onDone: () => void;
  /** The destination refused the create. Without it, a generic error toast. */
  readonly onDestinationProblem?: (problem: 'gone' | 'forbidden') => void;
  /** True while the create (and its scan photo upload) is in flight. */
  readonly onBusyChange?: (busy: boolean) => void;
}

/**
 * Add equipment, without its frame: the options, the phone handoff, online
 * search and the details form. The Create modal's equipment body renders it.
 * State lives as long as the panel is mounted, so a fresh mount is a fresh
 * start.
 */
export function AddEquipmentPanel({
  parentCollectionId,
  onDone,
  onDestinationProblem,
  onBusyChange,
}: AddEquipmentPanelProps) {
  const { isMobile } = useMobileDetection();
  const workspaceId = useWorkspaceId();
  const [step, setStep] = useState<AddEquipmentStep>('options');
  const [form, setForm] = useState<EquipmentFormState>(EMPTY_EQUIPMENT_FORM);
  const [scanConfidence, setScanConfidence] = useState<number | undefined>();
  const [scannedFile, setScannedFile] = useState<File | null>(null);
  // Held here, not in the search step, so Search -> Back -> Search keeps the
  // query and its results for as long as the panel is open.
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<ReadonlyArray<EquipmentSearchResult>>([]);
  // One "QR Displayed" per open of the panel: the step unmounts on Back, so a
  // ref inside it would fire again on QR -> Back -> QR.
  const qrDisplayTrackedRef = useRef(false);
  const { submit, isSubmitting, isBusy } = useAddEquipment({
    parentCollectionId,
    onDone,
    onDestinationProblem,
  });

  useEffect(() => {
    onBusyChange?.(isBusy);
  }, [isBusy, onBusyChange]);

  // Fired when the QR actually renders, not on the click that requests it, so
  // the funnel's "QR Displayed" means displayed. `has_workspace_scope` tells a
  // full handoff from a degraded one (no `ws` while the workspace hydrates).
  // The fired-ref also stops `workspaceId` hydrating null -> id from firing twice.
  useEffect(() => {
    if (step !== 'qr' || qrDisplayTrackedRef.current) return;
    qrDisplayTrackedRef.current = true;
    safeAnalytics.track(ANALYTICS_EVENTS.EQUIPMENT_PHONE_CAPTURE_QR_DISPLAYED, {
      workspace_id: workspaceId,
      has_workspace_scope: !!workspaceId,
    });
  }, [step, workspaceId]);

  const applyScan = (scan: EquipmentScan | null) => {
    if (scan) {
      const { make, model, serial } = scan;
      setForm((prev) => ({
        ...prev,
        make: make || prev.make,
        model: model || prev.model,
        serial: serial || prev.serial,
        title: prev.title || [make, model].filter(Boolean).join(' '),
      }));
      setScanConfidence(scan.confidence);
      setScannedFile(scan.file);
    }
    setStep('form');
  };

  const applySearchResult = (result: EquipmentSearchResult) => {
    setForm({
      title: result.title,
      make: result.make,
      model: result.model,
      serial: '',
      location: '',
    });
    setScanConfidence(undefined);
    setStep('form');
  };

  const back = () => setStep('options');

  switch (step) {
    case 'options':
      // "Photograph it with your phone" is pointless on the device that would do the scanning.
      return (
        <AddEquipmentOptionsStep showPhone={!isMobile} onChoose={setStep} onScanned={applyScan} />
      );
    case 'qr':
      return <AddEquipmentQrStep onBack={back} />;
    case 'search':
      return (
        <AddEquipmentSearchStep
          query={searchQuery}
          onQueryChange={setSearchQuery}
          results={searchResults}
          onResultsChange={setSearchResults}
          onSelect={applySearchResult}
          onBack={back}
        />
      );
    case 'form':
      return (
        <AddEquipmentFormStep
          form={form}
          onFieldChange={(field, value) => setForm((prev) => ({ ...prev, [field]: value }))}
          scanConfidence={scanConfidence}
          isSubmitting={isSubmitting}
          isBusy={isBusy}
          onSubmit={() => void submit({ form, scanConfidence, scannedFile })}
          onBack={back}
        />
      );
  }
}
